import React, { useState } from 'react';
import { Toggle, ToggleItem } from '@tremor/react';
import MovementPreadvisedPieChart from './movement-preadvised-pie-chart';
import MovementAirlinePieChart from './movement-airline-pie-chart';

const headerStyle = {
    border: '1px solid #e5e7eb',
    padding: '10px 14px',
    textAlign: 'center',
    backgroundColor: '#f9fafb',
    fontWeight: 600,
    fontSize: '13px',
    color: '#374151',
};

const cellStyle = {
    border: '1px solid #e5e7eb',
    padding: '10px 14px',
    textAlign: 'center',
    fontSize: '13px',
    color: '#1f2937',
};

const labelCellStyle = {
    ...cellStyle,
    textAlign: 'left',
    fontWeight: 500,
};

const tableStyle = {
    border: '1px solid #e5e7eb',
    borderCollapse: 'collapse',
    width: '100%',
};

const sectionTitleStyle = {
    fontSize: '14px',
    fontWeight: 600,
    color: '#1f2937',
    marginBottom: '8px',
    marginTop: '20px',
};

const subTitleStyle = {
    fontSize: '13px',
    fontWeight: 500,
    color: '#6b7280',
    marginBottom: '6px',
    marginTop: '18px',
};

const SummaryRow = ({ label, data }) => (
    <tr>
        <td style={cellStyle}>{label}</td>
        <td style={cellStyle}>{data.total}</td>
        <td style={cellStyle}>{data.preAdvised} ({data.preAdvisedPercentage}%)</td>
        <td style={cellStyle}>{data.nonAdvised} ({data.nonAdvisedPercentage}%)</td>
    </tr>
);

/** Single row per airline using total (outbound + inbound combined) */
const AirlineTotalRow = ({ rank, code, airline, data }) => (
    <tr>
        <td style={{ ...cellStyle, textAlign: 'center' }}>{rank}</td>
        <td style={cellStyle}>{airline ?? code}</td>
        <td style={cellStyle}>{data.total}</td>
        <td style={cellStyle}>{data.preAdvised} ({data.preAdvisedPercentage}%)</td>
        <td style={cellStyle}>{data.nonAdvised} ({data.nonAdvisedPercentage}%)</td>
    </tr>
);

/** Normalize airlines from array (backend) to object keyed by code (DirectionTable expects) */
const normalizeAirlines = (airlines) => {
    if (!airlines) return null;
    if (Array.isArray(airlines)) {
        return airlines.reduce((acc, a) => {
            if (a?.code != null) acc[a.code] = a;
            return acc;
        }, {});
    }
    return airlines;
};

const SummaryPieChartWithTabs = ({
    title,
    tabs,
    totalPreAdvised,
    totalNonAdvised,
    outboundPreAdvised,
    outboundNonAdvised,
    inboundPreAdvised,
    inboundNonAdvised,
}) => {
    const defaultTab = tabs[0]?.value ?? 'total';
    const [pieTab, setPieTab] = useState(defaultTab);
    const preAdvised = pieTab === 'total' ? totalPreAdvised : pieTab === 'outbound' ? outboundPreAdvised : inboundPreAdvised;
    const nonAdvised = pieTab === 'total' ? totalNonAdvised : pieTab === 'outbound' ? outboundNonAdvised : inboundNonAdvised;
    return (
        <div>
            <div style={{ marginBottom: '12px' }}>
                <Toggle color="zinc" value={pieTab} onValueChange={setPieTab}>
                    {tabs.map((tab) => (
                        <ToggleItem key={tab.value} value={tab.value} text={tab.text} />
                    ))}
                </Toggle>
            </div>
            <MovementPreadvisedPieChart title={title} preAdvised={preAdvised} nonAdvised={nonAdvised} />
        </div>
    );
};

const getAirlineTotal = (airline) => {
    if (!airline) return { total: 0, preAdvised: 0, nonAdvised: 0, preAdvisedPercentage: 0, nonAdvisedPercentage: 0 };
    if (airline.total != null && (airline.preAdvised != null || airline.outbound == null)) return airline;
    const ob = airline.outbound || {};
    const ib = airline.inbound || {};
    const total = (ob.total || 0) + (ib.total || 0);
    const preAdvised = (ob.preAdvised || 0) + (ib.preAdvised || 0);
    const nonAdvised = (ob.nonAdvised || 0) + (ib.nonAdvised || 0);
    return {
        total,
        preAdvised,
        nonAdvised,
        preAdvisedPercentage: total ? Math.round((preAdvised / total) * 10000) / 100 : 0,
        nonAdvisedPercentage: total ? Math.round((nonAdvised / total) * 10000) / 100 : 0,
    };
};

const ASSISTANCE_CODE_ORDER = [
    'BDGR', 'BLND', 'DEAF', 'DPNA', 'LNYD', 'MAAS', 'MEDA', 'PETC', 'PREBOARDHS',
    'WCBD', 'WCHC', 'WCHP', 'WCHR', 'WCHS', 'WCLB', 'WCMP', 'WELLCHK',
    'SVAN', 'STCR', 'WCBW', 'PPOC', 'ESAN', 'OXYG',
];

const buildAssistanceBreakdownLines = (esc, breakdown, label) => {
    if (!breakdown?.length) return [];
    const countsByCode = breakdown.reduce((acc, { assistanceCode, passengerCount }) => {
        acc[assistanceCode] = passengerCount;
        return acc;
    }, {});
    const ordered = ASSISTANCE_CODE_ORDER.filter((c) => countsByCode[c] != null);
    const others = Object.keys(countsByCode).filter((c) => !ASSISTANCE_CODE_ORDER.includes(c)).sort();
    const displayCodes = [...ordered, ...others];
    const total = Object.values(countsByCode).reduce((sum, n) => sum + n, 0);
    return [
        [label, ...displayCodes, 'Total'].map(esc).join(','),
        ['Count', ...displayCodes.map((c) => countsByCode[c] ?? 0), total].map(esc).join(','),
    ];
};

const getDisplayCodesFromBreakdowns = (...breakdowns) => {
    const allCodes = new Set();
    breakdowns.forEach((breakdown) => {
        (breakdown || []).forEach(({ assistanceCode }) => allCodes.add(assistanceCode));
    });
    const ordered = ASSISTANCE_CODE_ORDER.filter((c) => allCodes.has(c));
    const others = [...allCodes].filter((c) => !ASSISTANCE_CODE_ORDER.includes(c)).sort();
    return [...ordered, ...others];
};

const exportDailyAssistanceToCsv = (title, dailyBreakdown) => {
    if (!dailyBreakdown?.length) return;
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const lines = [];

    dailyBreakdown.forEach((day, index) => {
        if (index > 0) lines.push('');
        lines.push(esc(day.date));
        const outboundLines = buildAssistanceBreakdownLines(esc, day.outbound?.assistanceBreakdown, 'Outbound');
        const inboundLines = buildAssistanceBreakdownLines(esc, day.inbound?.assistanceBreakdown, 'Inbound');
        if (outboundLines.length || inboundLines.length) {
            lines.push(esc('Assistance Breakdown'));
            if (outboundLines.length) lines.push(...outboundLines);
            if (inboundLines.length) lines.push(...inboundLines);
        }
    });

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `movement-daily-assistance-${title.replace(/\s+/g, '-').toLowerCase()}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
};

const exportDailyAssistanceToExcel = async (title, dailyBreakdown) => {
    if (!dailyBreakdown?.length) return;
    const Excel = await import('exceljs/dist/exceljs.min.js');
    const workbook = new Excel.Workbook();

    const COLORS = { headerBg: 'FFF9FAFB', headerText: 'FF374151' };
    const border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    const applyCell = (cell, { fill, font, align = 'center' } = {}) => {
        cell.border = border;
        cell.alignment = { vertical: 'middle', horizontal: align, wrapText: true };
        if (fill) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
        if (font) cell.font = font;
    };

    dailyBreakdown.forEach((day) => {
        const outBreakdown = day.outbound?.assistanceBreakdown;
        const inBreakdown = day.inbound?.assistanceBreakdown;
        if (!outBreakdown?.length && !inBreakdown?.length) return;

        const displayCodes = getDisplayCodesFromBreakdowns(outBreakdown, inBreakdown);
        const sheet = workbook.addWorksheet(day.date);
        sheet.getColumn(1).width = 14;
        displayCodes.forEach((_, i) => { sheet.getColumn(i + 2).width = 10; });
        sheet.getColumn(displayCodes.length + 2).width = 10;

        const hdr = sheet.addRow(['Category', ...displayCodes, 'Total']);
        hdr.eachCell((cell) => applyCell(cell, { fill: COLORS.headerBg, font: { bold: true, color: { argb: COLORS.headerText }, size: 12 } }));
        applyCell(hdr.getCell(1), { fill: COLORS.headerBg, font: { bold: true, color: { argb: COLORS.headerText }, size: 12 }, align: 'left' });
        hdr.height = 24;

        const addBreakdownRow = (label, breakdown) => {
            if (!breakdown?.length) return;
            const countsByCode = breakdown.reduce((acc, { assistanceCode, passengerCount }) => { acc[assistanceCode] = passengerCount; return acc; }, {});
            const rowTotal = Object.values(countsByCode).reduce((sum, n) => sum + n, 0);
            const row = sheet.addRow([label, ...displayCodes.map((c) => countsByCode[c] ?? 0), rowTotal]);
            applyCell(row.getCell(1), { align: 'left' });
            for (let c = 2; c <= displayCodes.length + 2; c++) applyCell(row.getCell(c));
            row.height = 22;
        };
        addBreakdownRow('Outbound', outBreakdown);
        addBreakdownRow('Inbound', inBreakdown);
    });

    const buf = await workbook.xlsx.writeBuffer();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
    link.download = `movement-daily-assistance-${title.replace(/\s+/g, '-').toLowerCase()}.xlsx`;
    link.click();
    URL.revokeObjectURL(link.href);
};

const exportSummaryToCsv = (title, outbound, inbound, total, airlines) => {
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const lines = [];

    // Summary section
    lines.push(['Direction', 'Total', 'Pre-Advised', 'Non-Advised'].map(esc).join(','));
    const fmtRow = (label, d) => [label, d.total, `${d.preAdvised} (${d.preAdvisedPercentage}%)`, `${d.nonAdvised} (${d.nonAdvisedPercentage}%)`].map(esc).join(',');
    lines.push(fmtRow('Outbound', outbound));
    lines.push(fmtRow('Inbound', inbound));
    if (total) lines.push(fmtRow('Total', total));

    // Assistance breakdown sections
    const outboundBreakdownLines = buildAssistanceBreakdownLines(esc, outbound?.assistanceBreakdown, 'Outbound');
    const inboundBreakdownLines = buildAssistanceBreakdownLines(esc, inbound?.assistanceBreakdown, 'Inbound');
    if (outboundBreakdownLines.length || inboundBreakdownLines.length) {
        lines.push('');
        lines.push(esc('Assistance Breakdown'));
        if (outboundBreakdownLines.length) lines.push(...outboundBreakdownLines);
        if (inboundBreakdownLines.length) lines.push(...inboundBreakdownLines);
    }

    // Airlines section
    const norm = normalizeAirlines(airlines);
    if (norm && Object.keys(norm).length > 0) {
        lines.push('');
        lines.push(['Rank', 'Airline', 'Total', 'Pre-Advised', 'Non-Advised'].map(esc).join(','));
        Object.keys(norm).forEach((code, index) => {
            const d = getAirlineTotal(norm[code]);
            const displayName = norm[code]?.airline ?? code;
            lines.push([index + 1, displayName, d.total, `${d.preAdvised} (${d.preAdvisedPercentage}%)`, `${d.nonAdvised} (${d.nonAdvisedPercentage}%)`].map(esc).join(','));
        });
    }

    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `movement-summary-${title.replace(/\s+/g, '-').toLowerCase()}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
};

const exportSummaryToExcel = async (title, outbound, inbound, total, airlines) => {
    const Excel = await import('exceljs/dist/exceljs.min.js');
    const workbook = new Excel.Workbook();

    const COLORS = {
        headerBg: 'FFF9FAFB',
        headerText: 'FF374151',
        totalBg: 'FFF3F4F6',
        sectionBg: 'FF6B7280',
        whiteText: 'FFFFFFFF',
    };
    const border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    const applyCell = (cell, { fill, font, align = 'center' } = {}) => {
        cell.border = border;
        cell.alignment = { vertical: 'middle', horizontal: align, wrapText: true };
        if (fill) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
        if (font) cell.font = font;
    };

    // ── Sheet 1: Summary ──────────────────────────────────
    const s1 = workbook.addWorksheet('Summary');
    s1.getColumn(1).width = 16; s1.getColumn(2).width = 10; s1.getColumn(3).width = 22; s1.getColumn(4).width = 22;

    const s1Hdr = s1.addRow([title, 'Total', 'Pre-Advised', 'Non-Advised']);
    ['left', 'center', 'center', 'center'].forEach((align, i) => {
        applyCell(s1Hdr.getCell(i + 1), { fill: COLORS.headerBg, font: { bold: true, color: { argb: COLORS.headerText }, size: 12 }, align });
    });
    s1Hdr.height = 24;

    const addDirRow = (label, d, isTotalRow = false) => {
        const row = s1.addRow([label, d.total, `${d.preAdvised} (${d.preAdvisedPercentage}%)`, `${d.nonAdvised} (${d.nonAdvisedPercentage}%)`]);
        const fill = isTotalRow ? COLORS.totalBg : undefined;
        applyCell(row.getCell(1), { fill, font: isTotalRow ? { bold: true } : undefined, align: 'left' });
        [2, 3, 4].forEach(c => applyCell(row.getCell(c), { fill, font: isTotalRow ? { bold: true } : undefined }));
        row.height = 22;
    };
    addDirRow('Outbound', outbound);
    addDirRow('Inbound', inbound);
    if (total) addDirRow('Total', total, true);

    // ── Sheet 2: Assistance Breakdown (if present) ────────
    const outBreakdown = outbound?.assistanceBreakdown;
    const inBreakdown = inbound?.assistanceBreakdown;
    if (outBreakdown?.length || inBreakdown?.length) {
        const getDisplayCodes = (breakdown) => {
            if (!breakdown?.length) return [];
            const codes = breakdown.map(b => b.assistanceCode);
            const ordered = ASSISTANCE_CODE_ORDER.filter(c => codes.includes(c));
            const others = codes.filter(c => !ASSISTANCE_CODE_ORDER.includes(c)).sort();
            return [...ordered, ...others];
        };
        const allCodes = [...new Set([...getDisplayCodes(outBreakdown), ...getDisplayCodes(inBreakdown)])];
        const ordered = ASSISTANCE_CODE_ORDER.filter(c => allCodes.includes(c));
        const others = allCodes.filter(c => !ASSISTANCE_CODE_ORDER.includes(c)).sort();
        const displayCodes = [...ordered, ...others];

        const s2 = workbook.addWorksheet('Assistance Breakdown');
        s2.getColumn(1).width = 14;
        displayCodes.forEach((_, i) => { s2.getColumn(i + 2).width = 10; });
        s2.getColumn(displayCodes.length + 2).width = 10;

        const s2Hdr = s2.addRow(['Category', ...displayCodes, 'Total']);
        s2Hdr.eachCell((cell) => applyCell(cell, { fill: COLORS.headerBg, font: { bold: true, color: { argb: COLORS.headerText }, size: 12 } }));
        applyCell(s2Hdr.getCell(1), { fill: COLORS.headerBg, font: { bold: true, color: { argb: COLORS.headerText }, size: 12 }, align: 'left' });
        s2Hdr.height = 24;

        const addBreakdownRow = (label, breakdown) => {
            if (!breakdown?.length) return;
            const countsByCode = breakdown.reduce((acc, { assistanceCode, passengerCount }) => { acc[assistanceCode] = passengerCount; return acc; }, {});
            const rowTotal = Object.values(countsByCode).reduce((sum, n) => sum + n, 0);
            const row = s2.addRow([label, ...displayCodes.map(c => countsByCode[c] ?? 0), rowTotal]);
            applyCell(row.getCell(1), { align: 'left' });
            for (let c = 2; c <= displayCodes.length + 2; c++) applyCell(row.getCell(c));
            row.height = 22;
        };
        addBreakdownRow('Outbound', outBreakdown);
        addBreakdownRow('Inbound', inBreakdown);
    }

    // ── Sheet 3: Airlines (if present) ────────────────────
    const norm = normalizeAirlines(airlines);
    if (norm && Object.keys(norm).length > 0) {
        const s3 = workbook.addWorksheet('Airlines Breakdown');
        s3.getColumn(1).width = 8; s3.getColumn(2).width = 28; s3.getColumn(3).width = 12; s3.getColumn(4).width = 22; s3.getColumn(5).width = 22;

        const s3Hdr = s3.addRow(['Rank', 'Airline', 'Total', 'Pre-Advised', 'Non-Advised']);
        ['center', 'left', 'center', 'center', 'center'].forEach((align, i) => {
            applyCell(s3Hdr.getCell(i + 1), { fill: COLORS.headerBg, font: { bold: true, color: { argb: COLORS.headerText }, size: 12 }, align });
        });
        s3Hdr.height = 24;

        Object.keys(norm).forEach((code, index) => {
            const d = getAirlineTotal(norm[code]);
            const displayName = norm[code]?.airline ?? code;
            const rank = norm[code]?.rank ?? index + 1;
            const row = s3.addRow([rank, displayName, d.total, `${d.preAdvised} (${d.preAdvisedPercentage}%)`, `${d.nonAdvised} (${d.nonAdvisedPercentage}%)`]);
            applyCell(row.getCell(1));
            applyCell(row.getCell(2), { align: 'left' });
            [3, 4, 5].forEach(c => applyCell(row.getCell(c)));
            row.height = 22;
        });
    }

    const buf = await workbook.xlsx.writeBuffer();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
    link.download = `movement-summary-${title.replace(/\s+/g, '-').toLowerCase()}.xlsx`;
    link.click();
    URL.revokeObjectURL(link.href);
};

const exportAirlinesToExcel = async (title, airlines) => {
    const Excel = await import('exceljs/dist/exceljs.min.js');
    const workbook = new Excel.Workbook();
    const sheet = workbook.addWorksheet('Airlines Breakdown');

    const COLORS = { headerBg: 'FFF9FAFB', headerText: 'FF374151' };
    const border = { top: { style: 'thin' }, left: { style: 'thin' }, bottom: { style: 'thin' }, right: { style: 'thin' } };
    const applyCell = (cell, { fill, font, align = 'center' } = {}) => {
        cell.border = border;
        cell.alignment = { vertical: 'middle', horizontal: align, wrapText: true };
        if (fill) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } };
        if (font) cell.font = font;
    };

    sheet.getColumn(1).width = 8; sheet.getColumn(2).width = 28; sheet.getColumn(3).width = 12; sheet.getColumn(4).width = 22; sheet.getColumn(5).width = 22;

    const hdr = sheet.addRow(['Rank', 'Airline', 'Total', 'Pre-Advised', 'Non-Advised']);
    ['center', 'left', 'center', 'center', 'center'].forEach((align, i) => {
        applyCell(hdr.getCell(i + 1), { fill: COLORS.headerBg, font: { bold: true, color: { argb: COLORS.headerText }, size: 12 }, align });
    });
    hdr.height = 24;

    const norm = normalizeAirlines(airlines) || {};
    Object.keys(norm).forEach((code, index) => {
        const d = getAirlineTotal(norm[code]);
        const displayName = norm[code]?.airline ?? code;
        const rank = norm[code]?.rank ?? index + 1;
        const row = sheet.addRow([rank, displayName, d.total, `${d.preAdvised} (${d.preAdvisedPercentage}%)`, `${d.nonAdvised} (${d.nonAdvisedPercentage}%)`]);
        applyCell(row.getCell(1));
        applyCell(row.getCell(2), { align: 'left' });
        [3, 4, 5].forEach(c => applyCell(row.getCell(c)));
        row.height = 22;
    });

    const buf = await workbook.xlsx.writeBuffer();
    const link = document.createElement('a');
    link.href = URL.createObjectURL(new Blob([buf], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }));
    link.download = `airlines-breakdown-${title.replace(/\s+/g, '-').toLowerCase()}.xlsx`;
    link.click();
    URL.revokeObjectURL(link.href);
};

const exportAirlinesToCsv = (title, airlines) => {
    const esc = (v) => `"${String(v).replace(/"/g, '""')}"`;
    const lines = [];
    const norm = normalizeAirlines(airlines) || {};
    lines.push(['Rank', 'Airline', 'Total', 'Pre-Advised', 'Non-Advised'].map(esc).join(','));
    Object.keys(norm).forEach((code, index) => {
        const d = getAirlineTotal(norm[code]);
        const displayName = norm[code]?.airline ?? code;
        lines.push([index + 1, displayName, d.total, `${d.preAdvised} (${d.preAdvisedPercentage}%)`, `${d.nonAdvised} (${d.nonAdvisedPercentage}%)`].map(esc).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    link.href = URL.createObjectURL(blob);
    link.download = `airlines-breakdown-${title.replace(/\s+/g, '-').toLowerCase()}.csv`;
    link.click();
    URL.revokeObjectURL(link.href);
};

const DirectionTable = ({ title, outbound, inbound, total, airlines, pieChartTitle, dailyBreakdown }) => {
    const normalized = normalizeAirlines(airlines);
    const airlineCodes = normalized ? Object.keys(normalized) : [];
    const [isSummaryOpen, setIsSummaryOpen] = useState(true);
    const [isAirlinesOpen, setIsAirlinesOpen] = useState(false);

    const pieTotalPreAdvised = total?.preAdvised ?? ((outbound?.preAdvised ?? 0) + (inbound?.preAdvised ?? 0));
    const pieTotalNonAdvised = total?.nonAdvised ?? ((outbound?.nonAdvised ?? 0) + (inbound?.nonAdvised ?? 0));
    const pieOutboundPreAdvised = outbound?.preAdvised ?? 0;
    const pieOutboundNonAdvised = outbound?.nonAdvised ?? 0;
    const pieInboundPreAdvised = inbound?.preAdvised ?? 0;
    const pieInboundNonAdvised = inbound?.nonAdvised ?? 0;
    const hasTotal = (pieTotalPreAdvised + pieTotalNonAdvised) > 0;
    const hasOutbound = (pieOutboundPreAdvised + pieOutboundNonAdvised) > 0;
    const hasInbound = (pieInboundPreAdvised + pieInboundNonAdvised) > 0;
    const pieTabs = [
        ...(hasTotal ? [{ value: 'total', text: 'Total' }] : []),
        ...(hasOutbound ? [{ value: 'outbound', text: 'Outbound' }] : []),
        ...(hasInbound ? [{ value: 'arrival', text: 'Arrival' }] : []),
    ];
    const showDailyExport = dailyBreakdown?.length > 1;

    return (
        <div style={{ marginBottom: '24px' }}>
            {/* Collapsible summary section - same pattern as Airlines Breakdown and raw lists */}
            <div style={{
                border: '1px solid #e5e7eb',
                borderRadius: '8px',
                padding: '12px',
                marginBottom: '12px',
                backgroundColor: '#fff',
            }}>
                <div
                    onClick={() => setIsSummaryOpen(!isSummaryOpen)}
                    style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                        cursor: 'pointer',
                        userSelect: 'none',
                    }}
                >
                    <span style={{
                        display: 'inline-block',
                        fontSize: '12px',
                        color: '#9ca3af',
                        transition: 'transform 0.2s ease',
                        transform: isSummaryOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                    }}>
                        &#9654;
                    </span>
                    <p style={{ ...subTitleStyle, marginTop: 0, marginBottom: 0 }}>Summary</p>
                    <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); exportSummaryToCsv(title, outbound, inbound, total, normalized); }}
                            style={{
                                padding: '4px 10px',
                                fontSize: '12px',
                                cursor: 'pointer',
                                backgroundColor: '#f3f4f6',
                                color: '#374151',
                                border: '1px solid #e5e7eb',
                                borderRadius: '6px',
                            }}
                        >
                            Export CSV
                        </button>
                        <button
                            type="button"
                            onClick={(e) => { e.stopPropagation(); exportSummaryToExcel(title, outbound, inbound, total, normalized); }}
                            style={{
                                padding: '4px 10px',
                                fontSize: '12px',
                                cursor: 'pointer',
                                backgroundColor: '#dcfce7',
                                color: '#166534',
                                border: '1px solid #bbf7d0',
                                borderRadius: '6px',
                            }}
                        >
                            Export Excel
                        </button>
                        {showDailyExport && (
                            <>
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); exportDailyAssistanceToCsv(title, dailyBreakdown); }}
                                    style={{
                                        padding: '4px 10px',
                                        fontSize: '12px',
                                        cursor: 'pointer',
                                        backgroundColor: '#eff6ff',
                                        color: '#1d4ed8',
                                        border: '1px solid #bfdbfe',
                                        borderRadius: '6px',
                                    }}
                                >
                                    Daily Assistance CSV
                                </button>
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); exportDailyAssistanceToExcel(title, dailyBreakdown); }}
                                    style={{
                                        padding: '4px 10px',
                                        fontSize: '12px',
                                        cursor: 'pointer',
                                        backgroundColor: '#dbeafe',
                                        color: '#1e40af',
                                        border: '1px solid #93c5fd',
                                        borderRadius: '6px',
                                    }}
                                >
                                    Daily Assistance Excel
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {isSummaryOpen && (
                    <div style={{ marginTop: '10px' }}>
                        {pieChartTitle && pieTabs.length > 0 && (
                            <div style={{ marginBottom: '16px' }}>
                                {pieTabs.length > 1 ? (
                                    <SummaryPieChartWithTabs
                                        title={pieChartTitle}
                                        tabs={pieTabs}
                                        totalPreAdvised={pieTotalPreAdvised}
                                        totalNonAdvised={pieTotalNonAdvised}
                                        outboundPreAdvised={pieOutboundPreAdvised}
                                        outboundNonAdvised={pieOutboundNonAdvised}
                                        inboundPreAdvised={pieInboundPreAdvised}
                                        inboundNonAdvised={pieInboundNonAdvised}
                                    />
                                ) : (
                                    <MovementPreadvisedPieChart
                                        title={pieChartTitle}
                                        preAdvised={pieTabs[0]?.value === 'total' ? pieTotalPreAdvised : pieTabs[0]?.value === 'outbound' ? pieOutboundPreAdvised : pieInboundPreAdvised}
                                        nonAdvised={pieTabs[0]?.value === 'total' ? pieTotalNonAdvised : pieTabs[0]?.value === 'outbound' ? pieOutboundNonAdvised : pieInboundNonAdvised}
                                    />
                                )}
                            </div>
                        )}
                        <table style={tableStyle}>
                            <thead>
                                <tr>
                                    <th style={headerStyle}>{title}</th>
                                    <th style={headerStyle}>Total</th>
                                    <th style={headerStyle}>Pre-Advised</th>
                                    <th style={headerStyle}>Non-Advised</th>
                                </tr>
                            </thead>
                            <tbody>
                                <SummaryRow label="Outbound" data={outbound} />
                                <SummaryRow label="Inbound" data={inbound} />
                                {total && (
                                    <tr style={{ backgroundColor: '#f9fafb', fontWeight: 600 }}>
                                        <td style={cellStyle}>Total</td>
                                        <td style={cellStyle}>{total.total}</td>
                                        <td style={cellStyle}>{total.preAdvised} ({total.preAdvisedPercentage}%)</td>
                                        <td style={cellStyle}>{total.nonAdvised} ({total.nonAdvisedPercentage}%)</td>
                                    </tr>
                                )}
                            </tbody>
                        </table>
                    </div>
                )}
            </div>

            {airlineCodes.length > 0 && (
                <div style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '12px',
                    marginTop: '12px',
                    marginBottom: '12px',
                    backgroundColor: '#fff',
                }}>
                    {/* Collapsible section header with record count */}
                    <div
                        onClick={() => setIsAirlinesOpen(!isAirlinesOpen)}
                        style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '8px',
                            cursor: 'pointer',
                            userSelect: 'none',
                        }}
                    >
                        <span style={{
                            display: 'inline-block',
                            fontSize: '12px',
                            color: '#9ca3af',
                            transition: 'transform 0.2s ease',
                            transform: isAirlinesOpen ? 'rotate(90deg)' : 'rotate(0deg)',
                        }}>
                            &#9654;
                        </span>
                        <p style={{ ...subTitleStyle, marginTop: 0, marginBottom: 0 }}>Airlines Breakdown ({airlineCodes.length})</p>
                        <div style={{ marginLeft: 'auto', display: 'flex', gap: '6px' }}>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); exportAirlinesToCsv(title, normalized); }}
                                style={{
                                    padding: '4px 10px',
                                    fontSize: '12px',
                                    cursor: 'pointer',
                                    backgroundColor: '#f3f4f6',
                                    color: '#374151',
                                    border: '1px solid #e5e7eb',
                                    borderRadius: '6px',
                                }}
                            >
                                Export CSV
                            </button>
                            <button
                                type="button"
                                onClick={(e) => { e.stopPropagation(); exportAirlinesToExcel(title, normalized); }}
                                style={{
                                    padding: '4px 10px',
                                    fontSize: '12px',
                                    cursor: 'pointer',
                                    backgroundColor: '#dcfce7',
                                    color: '#166534',
                                    border: '1px solid #bbf7d0',
                                    borderRadius: '6px',
                                }}
                            >
                                Export Excel
                            </button>
                        </div>
                    </div>

                    {!isAirlinesOpen ? null : (
                        <div style={{ marginTop: '10px' }}>
                            <div style={{ marginBottom: '16px' }}>
                                <MovementAirlinePieChart airlines={normalized} title={title} />
                            </div>
                            <div style={{ maxHeight: '400px', overflowY: 'auto', overflowX: 'auto' }}>
                                <table style={{ ...tableStyle, tableLayout: 'fixed' }}>
                                <colgroup>
                                    <col style={{ width: '10%' }} />
                                    <col style={{ width: '22.5%' }} />
                                    <col style={{ width: '22.5%' }} />
                                    <col style={{ width: '22.5%' }} />
                                    <col style={{ width: '22.5%' }} />
                                </colgroup>
                                <thead>
                                    <tr>
                                        <th style={{ ...headerStyle, textAlign: 'center', position: 'sticky', top: 0, zIndex: 1 }}>Rank</th>
                                        <th style={{ ...headerStyle, position: 'sticky', top: 0, zIndex: 1 }}>Airline</th>
                                        <th style={{ ...headerStyle, position: 'sticky', top: 0, zIndex: 1 }}>Total</th>
                                        <th style={{ ...headerStyle, position: 'sticky', top: 0, zIndex: 1 }}>Pre-Advised</th>
                                        <th style={{ ...headerStyle, position: 'sticky', top: 0, zIndex: 1 }}>Non-Advised</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {airlineCodes.map((code, index) => (
                                        <AirlineTotalRow key={code} rank={(normalized[code]?.rank ?? index + 1)} code={code} airline={normalized[code]?.airline ?? code} data={getAirlineTotal(normalized[code])} />
                                    ))}
                                </tbody>
                            </table>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

const MovementSummary = ({ dailyReports, dailyBreakdown }) => {
    if (!dailyReports?.length) return <p>No movement data available.</p>;

    return (
        <div>
            {/* single aggregated summary for the date range */}
            {dailyReports.map((report) => (
                <DirectionTable
                    key={report.date}
                    title={report.date}
                    pieChartTitle={`Total Preadvised vs. Non-Advised (${report.date})`}
                    outbound={report.outbound}
                    inbound={report.inbound}
                    total={report.total}
                    airlines={report.airlines}
                    dailyBreakdown={dailyBreakdown}
                />
            ))}
        </div>
    );
};

export default MovementSummary;
